// Navigation and ephemeral disclosure state only. No generation or reading-store writes.
export class ReportReader {
    public message = '';
    private context = '';
    private restored = '';
    private closed = new Set<string>();
    reset(context = '') {
        if (context === this.context) return;
        this.context = context; this.restored = ''; this.closed.clear(); this.message = '';
    }
    isOpen(id: string) { return !this.closed.has(id); }
    toggle(id: string, event: Event) {
        (event.target as HTMLDetailsElement).open ? this.closed.delete(id) : this.closed.add(id);
    }
    expand() {
        this.closed.clear();
        document.querySelectorAll<HTMLDetailsElement>('.run-chapter details').forEach(el => el.open = true);
        this.message = '모든 장의 본문·근거·제한을 펼쳤습니다. 브라우저 찾기를 사용할 수 있습니다.';
    }
    go(id: string, event?: Event) {
        event?.preventDefault();
        const target = document.getElementById(id);
        if (!target) { this.message = '이 버전에서 참조 위치를 찾지 못했습니다. 다른 버전의 문단으로 대신 이동하지 않습니다.'; return; }
        for (let parent = target.parentElement; parent; parent = parent.parentElement) {
            if (parent instanceof HTMLDetailsElement) { parent.open = true; if (parent.id) this.closed.delete(parent.id); }
        }
        target.scrollIntoView({ block: 'start' });
        target.focus({ preventScroll: true });
        history.replaceState(history.state, '', location.pathname + location.search + '#' + id);
        this.restored = id; this.message = '';
    }
    async stableGo(id: string, cancelled: () => boolean, relative = 0) {
        const target = document.getElementById(id);
        if (!target) { this.message = '이 버전에서 문단을 이용할 수 없습니다. 저장 항목을 정리할 수 있습니다.'; return false; }
        for (let parent = target.parentElement; parent; parent = parent.parentElement) {
            if (parent instanceof HTMLDetailsElement) { parent.open = true; if (parent.id) this.closed.delete(parent.id); }
        }
        const images = Array.from(document.images).filter(img => !img.complete && !!(img.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING));
        const assets = Promise.all([document.fonts?.ready, ...images.map(img => new Promise<void>(resolve => {
            img.addEventListener('load', () => resolve(), { once: true }); img.addEventListener('error', () => resolve(), { once: true });
        }))]);
        let timeout: any;
        const ready = await Promise.race([assets.then(() => true), new Promise<boolean>(resolve => timeout = setTimeout(() => resolve(false), 8000))]);
        clearTimeout(timeout);
        if (!ready) { this.message = '본문 표시를 기다리고 있습니다. 준비되면 이어 읽기를 다시 눌러 주세요.'; return false; }
        let last = '', stable = 0;
        for (let i = 0; i < 40 && stable < 3; i++) {
            if (cancelled() || !target.isConnected) return false;
            await new Promise(resolve => setTimeout(resolve, 50));
            const rect = target.getBoundingClientRect(), value = [rect.top + scrollY, rect.height, rect.width].join(':');
            stable = value === last ? stable + 1 : 0; last = value;
        }
        if (cancelled() || !target.isConnected || stable < 3) return false;
        this.go(id);
        if (relative) {
            const rect = target.getBoundingClientRect();
            window.scrollBy(0, rect.top + rect.height * relative - Math.min(120, innerHeight * .2));
        }
        return true;
    }
    capture() {
        // Prefer the visible paragraph over its chapter container: content can be
        // inserted above the paragraph without moving the chapter's own top.
        const targets = [
            ...Array.from(document.querySelectorAll<HTMLElement>('.reader-paragraph, tr[data-reader-anchor]')),
            ...Array.from(document.querySelectorAll<HTMLElement>('[data-reader-anchor]'))
        ];
        const target = targets.find(el => {
            const rect = el.getBoundingClientRect(); return rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight;
        });
        return target ? { id: target.id, top: target.getBoundingClientRect().top, scroll: scrollY, context: this.context } : null;
    }
    maintain(anchor: any) {
        if (!anchor || anchor.context !== this.context || Math.abs(scrollY - anchor.scroll) > 2) return;
        const target = document.getElementById(anchor.id);
        if (target) window.scrollBy(0, target.getBoundingClientRect().top - anchor.top);
    }
}

const references = new WeakMap<object, any>();
export function readingReference(chapter: any, item: any, paragraph: any) {
    const cached = references.get(paragraph);
    if (cached?.chapter_version === chapter.version && cached?.run_id === chapter.reader_reference.run_id) return cached;
    const value = { ...chapter.reader_reference, source_run_id: item.source_run_id,
        item_id: item.id, task_id: item.task_id, result_ref: item.result_ref,
        paragraph_id: paragraph.id, block_index: paragraph.index,
        text_start: paragraph.start, text_end: paragraph.end };
    references.set(paragraph, value);
    return value;
}

// Recognize only plain pipe tables; all other content remains escaped, intact text.
export function tableBlock(text: string): string[][] | null {
    const lines = text.split('\n');
    if (lines.length < 3 || !/^\s*\|?\s*:?-{3,}/.test(lines[1]) || !lines.every(line => line.includes('|'))) return null;
    const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cell => cell.trim());
    const separator = cells(lines[1]);
    if (!separator.every(cell => /^:?-{3,}:?$/.test(cell))) return null;
    const rows = lines.filter((_, i) => i !== 1).map(cells);
    return rows.every(row => row.length === separator.length) ? rows : null;
}
